// src/pages/app/BotChannelsPage.tsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BotChannel,
  ChannelType,
  fetchChannels,
  createChannel,
  updateChannel,
  deleteChannel,
  getMetaConnectUrl,
  getMetaSession,
  attachMetaSession,
  MetaSessionResponse,
  completeWhatsappEmbeddedSignup,
  attachWhatsappSession,
  WhatsappConnectSessionResponse
} from "../../api/bots";
import { loadFacebookSdk } from "../../utils/facebookSdk";
import { useTranslation } from "react-i18next";

const channelTypes: ChannelType[] = ["WEB", "WHATSAPP", "FACEBOOK", "INSTAGRAM"];

function isMetaChannelType(type: ChannelType) {
  return type === "FACEBOOK" || type === "INSTAGRAM";
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const BotChannelsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [channels, setChannels] = useState<BotChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<{
    type: ChannelType;
    externalId: string;
    accessToken: string;
    meta: string;
  }>({
    type: "WEB",
    externalId: "",
    accessToken: "",
    meta: ""
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    externalId: string;
    accessToken: string;
    meta: string;
  }>({
    externalId: "",
    accessToken: "",
    meta: ""
  });

  // Meta connect session state (page selection)
  const [metaSession, setMetaSession] = useState<MetaSessionResponse | null>(
    null
  );
  const [metaSessionLoading, setMetaSessionLoading] = useState(false);
  const [metaSessionError, setMetaSessionError] = useState<string | null>(null);
  const [metaSelectedPageId, setMetaSelectedPageId] = useState<string>("");
  const [metaAttachLoading, setMetaAttachLoading] = useState(false);

  // WhatsApp embedded signup session state
  const [waSession, setWaSession] =
    useState<WhatsappConnectSessionResponse | null>(null);
  const [waSelectedNumberId, setWaSelectedNumberId] = useState<string>("");
  const [waConnecting, setWaConnecting] = useState(false);
  const [waAttachLoading, setWaAttachLoading] = useState(false);
  const [waRegistrationPin, setWaRegistrationPin] = useState<string>("");

  // Meta permissions confirmation dialog
  const [metaPermissionsChannel, setMetaPermissionsChannel] = useState<
    "FACEBOOK" | "INSTAGRAM" | null
  >(null);
  const [metaPermissionsAccepted, setMetaPermissionsAccepted] =
    useState(false);
  const [metaPermissionsSubmitting, setMetaPermissionsSubmitting] =
    useState(false);

  // Meta page select modal
  const [metaSelectModalOpen, setMetaSelectModalOpen] = useState(false);

  const clearMetaSessionFromUrl = () => {
    const params = new URLSearchParams(location.search);
    if (params.has("metaSessionId")) {
      params.delete("metaSessionId");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString()
        },
        { replace: true }
      );
    }
  };

  const closeMetaSelectModal = () => {
    setMetaSelectModalOpen(false);
    setMetaSession(null);
    setMetaSelectedPageId("");
    clearMetaSessionFromUrl();
  };

  const openMetaPermissionsDialog = (channel: "FACEBOOK" | "INSTAGRAM") => {
    setMetaPermissionsChannel(channel);
    setMetaPermissionsAccepted(false);
    setMetaPermissionsSubmitting(false);
  };

  const closeMetaPermissionsDialog = () => {
    setMetaPermissionsChannel(null);
    setMetaPermissionsAccepted(false);
    setMetaPermissionsSubmitting(false);
  };

  const handleConfirmMetaPermissions = async () => {
    if (!id || !metaPermissionsChannel) return;

    setMetaPermissionsSubmitting(true);
    setError(null);

    try {
      const returnPath = `/app/bots/${encodeURIComponent(id)}/channels`;
      const { url } = await getMetaConnectUrl(
        id,
        metaPermissionsChannel,
        returnPath
      );
      window.location.href = url;
    } catch (err: any) {
      console.error(err);
      const errorKey =
        metaPermissionsChannel === "FACEBOOK"
          ? "botChannels.errors.facebookConnectFailed"
          : "botChannels.errors.instagramConnectFailed";
      setError(err?.message || t(errorKey));
      setMetaPermissionsSubmitting(false);
    }
  };

  const loadChannels = () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetchChannels(id)
      .then((data) => setChannels(data))
      .catch((err: any) => {
        console.error(err);
        setError(err?.message || t("botChannels.errors.loadFailed"));
      })
      .finally(() => setLoading(false));
  };

  const loadMetaSessionFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("metaSessionId");
    if (!sessionId) {
      setMetaSession(null);
      setMetaSessionError(null);
      setMetaSelectedPageId("");
      setMetaSelectModalOpen(false);
      return;
    }

    setMetaSessionLoading(true);
    setMetaSessionError(null);
    getMetaSession(sessionId)
      .then((session) => {
        setMetaSession(session);
        console.log("Meta session for", session.channelType, session);
        setMetaSelectedPageId("");
        setMetaSelectModalOpen(true);
      })
      .catch((err: any) => {
        console.error(err);
        setMetaSessionError(
          err?.message || t("botChannels.errors.metaSessionLoad")
        );
        setMetaSession(null);
        setMetaSelectModalOpen(false);
      })
      .finally(() => setMetaSessionLoading(false));
  };

  useEffect(() => {
    loadChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadMetaSessionFromQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (!id) {
    return (
      <div className="page-container">
        <p>{t("botChannels.missingId")}</p>
      </div>
    );
  }

  const handleCreateChange =
    (field: keyof typeof createForm) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      setCreateForm({
        ...createForm,
        [field]: e.target.value
      });
    };

  const handleEditChange =
    (field: keyof typeof editForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setEditForm({
        ...editForm,
        [field]: e.target.value
      });
    };

  const handleCreateSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!id) return;
    setError(null);

    try {
      const metaTypeSelected = isMetaChannelType(createForm.type);

      if (metaTypeSelected) {
        throw new Error(t("botChannels.errors.metaMustUseConnect"));
      }

      let meta: any = undefined;
      if (createForm.meta.trim()) {
        meta = JSON.parse(createForm.meta);
      }

      await createChannel(id, {
        type: createForm.type,
        externalId: createForm.externalId,
        accessToken: createForm.accessToken,
        meta
      });

      setCreateForm({
        type: "WEB",
        externalId: "",
        accessToken: "",
        meta: ""
      });
      setCreating(false);
      loadChannels();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.createFailed"));
    }
  };

  const startEdit = (ch: BotChannel) => {
    if (isMetaChannelType(ch.type)) {
      setError(t("botChannels.errors.metaManagedExternally"));
      return;
    }

    setEditingId(ch.id);
    setEditForm({
      externalId: ch.externalId,
      accessToken: ch.accessToken,
      meta: ch.meta ? JSON.stringify(ch.meta, null, 2) : ""
    });
  };

  const handleEditSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!id || !editingId) return;
    setError(null);

    try {
      let meta: any = undefined;
      if (editForm.meta.trim()) {
        meta = JSON.parse(editForm.meta);
      }

      await updateChannel(id, editingId, {
        externalId: editForm.externalId,
        accessToken: editForm.accessToken,
        meta
      });

      setEditingId(null);
      loadChannels();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.updateFailed"));
    }
  };

  const handleDelete = async (channelId: string) => {
    if (!id) return;
    if (!window.confirm(t("botChannels.confirm.deleteChannel"))) return;

    setError(null);
    try {
      await deleteChannel(id, channelId);
      loadChannels();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.deleteFailed"));
    }
  };

  const metaTypeSelectedInCreate = isMetaChannelType(createForm.type);

  const handleAttachMetaSession = async () => {
    if (!metaSession || !metaSelectedPageId) return;
    setMetaAttachLoading(true);
    setError(null);

    try {
      await attachMetaSession(metaSession.id, metaSelectedPageId);
      setMetaSession(null);
      setMetaSelectedPageId("");
      clearMetaSessionFromUrl();
      setMetaSelectModalOpen(false);
      loadChannels();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.metaAttachFailed"));
    } finally {
      setMetaAttachLoading(false);
    }
  };

  const handleConnectWhatsApp = async () => {
    if (!id) return;
    setError(null);
    setWaConnecting(true);

    const appId =
      (import.meta as any).env.VITE_META_APP_ID ||
      (import.meta as any).env.VITE_FACEBOOK_APP_ID;
    const configId = (import.meta as any).env.VITE_WHATSAPP_EMBEDDED_CONFIG_ID;
    const redirectUri =
      (import.meta as any).env.VITE_WHATSAPP_EMBEDDED_REDIRECT_URI ||
      `${window.location.origin}/app/bots`;

    if (!appId || !configId) {
      setError(t("botChannels.errors.whatsappEnvMissing"));
      setWaConnecting(false);
      return;
    }

    try {
      const lastConnectTimestampStr =
        window.localStorage.getItem("wa_last_connect_ts");
      if (lastConnectTimestampStr) {
        const lastConnectTimestamp = parseInt(lastConnectTimestampStr, 10);
        if (Date.now() - lastConnectTimestamp < SEVEN_DAYS_MS) {
          if (
            !window.confirm(
              t("botChannels.whatsapp.confirmReconnectWithinWeek")
            )
          ) {
            setWaConnecting(false);
            return;
          }
        }
      }

      const FB = await loadFacebookSdk(appId);

      console.log("redirectUri", redirectUri, "origin", window.location.origin);

      FB.login(
        (response: any) => {
          (async () => {
            if (
              !response ||
              !response.authResponse ||
              !response.authResponse.code
            ) {
              setError(t("botChannels.errors.whatsappCancelled"));
              setWaConnecting(false);
              return;
            }

            const code = response.authResponse.code as string;

            try {
              const session = await completeWhatsappEmbeddedSignup(id, {
                code,
                redirectUri
              });
              setWaSession(session);
              setWaSelectedNumberId("");
              setWaRegistrationPin("");
            } catch (err: any) {
              console.error(err);
              setError(
                err?.message || t("botChannels.errors.whatsappSignupFailed")
              );
            } finally {
              setWaConnecting(false);
            }
          })();
        },
        {
          config_id: configId,
          redirect_uri: redirectUri,
          response_type: "code",
          override_default_response_type: true
        }
      );

      window.localStorage.setItem(
        "wa_last_connect_ts",
        Date.now().toString()
      );
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.whatsappInitFailed"));
      setWaConnecting(false);
    }
  };

  const handleAttachWhatsappSession = async () => {
    if (!waSession || !waSelectedNumberId) return;
    setWaAttachLoading(true);
    setError(null);

    try {
      await attachWhatsappSession(
        waSession.sessionId,
        waSelectedNumberId,
        waRegistrationPin || undefined
      );
      setWaSession(null);
      setWaSelectedNumberId("");
      setWaRegistrationPin("");
      loadChannels();
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botChannels.errors.whatsappAttachFailed"));
    } finally {
      setWaAttachLoading(false);
    }
  };

  const renderChannelDetails = (ch: BotChannel) => {
    if (isMetaChannelType(ch.type)) {
      const meta = (ch.meta as any) || {};
      const pageName = meta.pageName as string | undefined;
      const igUsername = meta.igUsername as string | undefined;
      const igName = meta.igName as string | undefined;

      const label =
        ch.type === "FACEBOOK"
          ? t("botChannels.labels.facebookPage")
          : t("botChannels.labels.instagramBusiness");

      const displayName =
        ch.type === "FACEBOOK"
          ? pageName
          : igUsername || igName || pageName;

      if (displayName) {
        return (
          <>
            <strong>{label}</strong>
            <div className="muted">{displayName}</div>
          </>
        );
      }

      return (
        <>
          <strong>{label}</strong>
          <div className="muted">
            {t("botChannels.meta.connectedViaMeta")}
          </div>
        </>
      );
    }

    if (ch.type === "WHATSAPP") {
      const displayPhoneNumber =
        (ch.meta?.displayPhoneNumber as string | undefined) ||
        (ch.meta?.display_phone_number as string | undefined);
      const verifiedName =
        (ch.meta?.verifiedName as string | undefined) ||
        (ch.meta?.verified_name as string | undefined);

      return (
        <>
          <strong>{t("botChannels.labels.whatsapp")}</strong>
          <div className="muted">
            {displayPhoneNumber
              ? t("botChannels.whatsapp.number", { number: displayPhoneNumber })
              : t("botChannels.whatsapp.numberId", { id: ch.externalId })}
            {verifiedName ? ` – ${verifiedName}` : ""}
          </div>
        </>
      );
    }

    if (ch.type === "WEB") {
      return (
        <>
          <strong>{t("botChannels.labels.webWidget")}</strong>
          <div className="muted">
            {t("botChannels.web.externalId", {
              id: ch.externalId || t("botChannels.web.defaultWidget")
            })}
          </div>
        </>
      );
    }

    return (
      <>
        <strong>{ch.type}</strong>
        <div className="muted">
          {t("botChannels.generic.externalId", { id: ch.externalId })}
        </div>
      </>
    );
  };

  const getChannelStatusInfo = (
    ch: BotChannel | null
  ): { label: string; className: string; isConnected: boolean } => {
    if (!ch) {
      return {
        label: t("botChannels.status.notConnected"),
        className: "status-badge status-badge-warn",
        isConnected: false
      };
    }

    const meta = (ch.meta as any) || {};

    if (!isMetaChannelType(ch.type)) {
      const needsReconnect = meta.needsReconnect === true;
      if (needsReconnect) {
        return {
          label: t("botChannels.status.needsReconnect"),
          className: "status-badge status-badge-error",
          isConnected: false
        };
      }
      return {
        label: t("botChannels.status.active"),
        className: "status-badge status-badge-ok",
        isConnected: true
      };
    }

    const now = Date.now();
    const needsReconnect = meta.needsReconnect === true;
    const tokenExpiresAtStr: string | undefined = meta.tokenExpiresAt;
    let expiresAt: Date | null = null;

    if (tokenExpiresAtStr) {
      const d = new Date(tokenExpiresAtStr);
      if (!isNaN(d.getTime())) expiresAt = d;
    }

    if (needsReconnect) {
      return {
        label: t("botChannels.status.needsReconnect"),
        className: "status-badge status-badge-error",
        isConnected: false
      };
    }

    if (!expiresAt) {
      return {
        label: t("botChannels.status.connectedUnknownExpiry"),
        className: "status-badge status-badge-ok",
        isConnected: true
      };
    }

    const diff = expiresAt.getTime() - now;

    if (diff <= 0) {
      return {
        label: t("botChannels.status.expired"),
        className: "status-badge status-badge-error",
        isConnected: false
      };
    }

    if (diff <= SEVEN_DAYS_MS) {
      return {
        label: t("botChannels.status.expiringSoon"),
        className: "status-badge status-badge-warn",
        isConnected: false
      };
    }

    return {
      label: t("botChannels.status.connected"),
      className: "status-badge status-badge-ok",
      isConnected: true
    };
  };

  const renderChannelStatusBadge = (ch: BotChannel | null) => {
    const { label, className } = getChannelStatusInfo(ch);
    return <span className={className}>{label}</span>;
  };

  const facebookChannel = channels.find((c) => c.type === "FACEBOOK") || null;
  const instagramChannel = channels.find((c) => c.type === "INSTAGRAM") || null;
  const whatsappChannel = channels.find((c) => c.type === "WHATSAPP") || null;

  const fbStatus = getChannelStatusInfo(facebookChannel);
  const igStatus = getChannelStatusInfo(instagramChannel);
  const waStatus = getChannelStatusInfo(whatsappChannel);

  const connectedChannelLabels = {
    type: t("botChannels.table.type"),
    details: t("botChannels.table.details"),
    status: t("botChannels.table.status"),
    actions: t("botChannels.table.actions", "Actions")
  };

  const isValidWaPin = /^\d{6}$/.test(waRegistrationPin);

  return (
    <div>
      <div className="page-header">
        {/* Meta permissions dialog */}
        {metaPermissionsChannel && (
          <div className="meta-permissions-modal-backdrop">
            <div className="meta-permissions-modal">
              <div className="meta-permissions-modal-header">
                <h2>
                  {metaPermissionsChannel === "FACEBOOK"
                    ? t("botChannels.meta.permissionsDialog.titleFacebook")
                    : t("botChannels.meta.permissionsDialog.titleInstagram")}
                </h2>
                <button
                  type="button"
                  className="btn-ghost small"
                  onClick={closeMetaPermissionsDialog}
                >
                  {t("botChannels.actions.cancel")}
                </button>
              </div>

              <div className="meta-permissions-modal-body">
                <p className="muted">
                  {metaPermissionsChannel === "FACEBOOK"
                    ? t("botChannels.meta.permissionsDialog.introFacebook")
                    : t("botChannels.meta.permissionsDialog.introInstagram")}
                </p>

                <p className="meta-permissions-list-title">
                  {t("botChannels.meta.permissionsDialog.listTitle")}
                </p>

                {metaPermissionsChannel === "FACEBOOK" ? (
                  <ul className="meta-permissions-list">
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.facebook.pagesShowList"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.facebook.pagesMessaging"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.facebook.pagesManageMetadata"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.facebook.businessManagement"
                      )}
                    </li>
                  </ul>
                ) : (
                  <ul className="meta-permissions-list">
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.pagesShowList"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.instagramBasic"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.instagramManageMessages"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.pagesManageMetadata"
                      )}
                    </li>
                    <li>
                      {t(
                        "botChannels.meta.permissionsDialog.instagram.businessManagement"
                      )}
                    </li>
                  </ul>
                )}

                <label
                  className="checkbox-inline"
                  style={{ marginTop: "0.75rem" }}
                >
                  <input
                    type="checkbox"
                    checked={metaPermissionsAccepted}
                    onChange={(e) =>
                      setMetaPermissionsAccepted(e.target.checked)
                    }
                  />
                  <span>
                    {t("botChannels.meta.permissionsDialog.checkboxLabel")}
                  </span>
                </label>

                <p className="muted small" style={{ marginTop: "0.5rem" }}>
                  {t("botChannels.meta.permissionsDialog.footerNote")}
                </p>
              </div>

              <div className="meta-permissions-modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeMetaPermissionsDialog}
                >
                  {t("botChannels.actions.cancel")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    !metaPermissionsAccepted || metaPermissionsSubmitting
                  }
                  onClick={handleConfirmMetaPermissions}
                >
                  {metaPermissionsChannel === "FACEBOOK"
                    ? t(
                        "botChannels.meta.permissionsDialog.continueFacebook"
                      )
                    : t(
                        "botChannels.meta.permissionsDialog.continueInstagram"
                      )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Meta session page selection modal */}
        {metaSession && metaSelectModalOpen && (
          <div className="meta-page-select-modal-backdrop">
            <div className="meta-page-select-modal">
              <div className="meta-page-select-modal-header">
                <h2>
                  {metaSession.channelType === "FACEBOOK"
                    ? t("botChannels.meta.selectModal.titleFacebook")
                    : t("botChannels.meta.selectModal.titleInstagram")}
                </h2>
                <button
                  type="button"
                  className="btn-ghost small"
                  onClick={closeMetaSelectModal}
                >
                  {t("botChannels.actions.cancel")}
                </button>
              </div>

              <div className="meta-page-select-modal-body">
                <p className="muted">
                  {metaSession.channelType === "FACEBOOK"
                    ? t("botChannels.meta.selectModal.introFacebook")
                    : t("botChannels.meta.selectModal.introInstagram")}
                </p>

                {metaSessionLoading && (
                  <p className="muted">
                    {t("botChannels.meta.loadingPages")}
                  </p>
                )}
                {metaSessionError && (
                  <div className="form-error">
                    {t("botChannels.meta.sessionLoadFailed")}:{" "}
                    {metaSessionError}
                  </div>
                )}

                {!metaSessionLoading &&
                  !metaSessionError &&
                  (() => {
                    const eligiblePages = metaSession.pages.filter((p) =>
                      metaSession.channelType === "INSTAGRAM"
                        ? !!p.instagramBusinessId
                        : true
                    );

                    const personalPages = eligiblePages.filter(
                      (p) => !p.isBusinessManaged
                    );
                    const businessPages = eligiblePages.filter(
                      (p) => p.isBusinessManaged
                    );

                    const hasAnyPages =
                      metaSession.pages && metaSession.pages.length > 0;
                    const isWeirdInstagramState =
                      metaSession.channelType === "INSTAGRAM" &&
                      hasAnyPages &&
                      eligiblePages.length === 0;

                    if (eligiblePages.length === 0) {
                      return (
                        <>
                          <p className="muted">
                            {t("botChannels.meta.selectModal.noPages")}
                          </p>

                          {isWeirdInstagramState && (
                            <p
                              className="muted small"
                              style={{ marginTop: "0.5rem" }}
                            >
                              {t(
                                "botChannels.meta.selectModal.editPreviousSettingsHint"
                              )}
                            </p>
                          )}
                        </>
                      );
                    }

                    return (
                      <>
                        {personalPages.length > 0 && (
                          <>
                            <p className="meta-page-select-group-title">
                              {t(
                                "botChannels.meta.selectModal.personalGroupTitle"
                              )}
                            </p>
                            <div className="meta-page-select-list">
                              {personalPages.map((p) => (
                                <label
                                  key={p.id}
                                  className={
                                    "meta-page-select-item" +
                                    (metaSelectedPageId === p.id
                                      ? " selected"
                                      : "")
                                  }
                                >
                                  <input
                                    type="radio"
                                    name="meta-page-select"
                                    value={p.id}
                                    checked={metaSelectedPageId === p.id}
                                    onChange={() =>
                                      setMetaSelectedPageId(p.id)
                                    }
                                  />
                                  <span className="meta-page-select-name">
                                    {p.name}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        )}

                        {businessPages.length > 0 && (
                          <>
                            <p className="meta-page-select-group-title">
                              {t(
                                "botChannels.meta.selectModal.businessGroupTitle"
                              )}
                            </p>
                            <div className="meta-page-select-list">
                              {businessPages.map((p) => (
                                <label
                                  key={p.id}
                                  className={
                                    "meta-page-select-item" +
                                    (metaSelectedPageId === p.id
                                      ? " selected"
                                      : "")
                                  }
                                >
                                  <input
                                    type="radio"
                                    name="meta-page-select"
                                    value={p.id}
                                    checked={metaSelectedPageId === p.id}
                                    onChange={() =>
                                      setMetaSelectedPageId(p.id)
                                    }
                                  />
                                  <span className="meta-page-select-name">
                                    {p.name}
                                    {p.businessName
                                      ? ` (${p.businessName})`
                                      : ""}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
              </div>

              <div className="meta-page-select-modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeMetaSelectModal}
                >
                  {t("botChannels.actions.cancel")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!metaSelectedPageId || metaAttachLoading}
                  onClick={handleAttachMetaSession}
                >
                  {metaAttachLoading
                    ? t("botChannels.actions.connecting")
                    : t("botChannels.actions.connectSelected")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp PIN + number modal (reuses meta modal styles) */}
        {waSession && (
          <div className="meta-page-select-modal-backdrop">
            <div className="meta-page-select-modal">
              <div className="meta-page-select-modal-header">
                <h2>
                  {t(
                    "botChannels.whatsapp.pinModal.title",
                    "Connect WhatsApp Business"
                  )}
                </h2>
                <button
                  type="button"
                  className="btn-ghost small"
                  onClick={() => {
                    setWaSession(null);
                    setWaSelectedNumberId("");
                    setWaRegistrationPin("");
                  }}
                >
                  {t("botChannels.actions.cancel")}
                </button>
              </div>

              <div className="meta-page-select-modal-body">
                <p className="muted">
                  {t(
                    "botChannels.whatsapp.pinModal.intro",
                    "Select the WhatsApp Business phone number you want to connect and enter the two-step verification PIN for that number."
                  )}
                </p>

                <p className="muted small" style={{ marginTop: "0.5rem" }}>
                  {t(
                    "botChannels.whatsapp.pinModal.whatIsPin",
                    "This PIN is the 6-digit two-step verification PIN configured for this phone number in WhatsApp Manager, not your Google Authenticator code or login 2FA."
                  )}
                </p>

                <ol className="muted small" style={{ marginTop: "0.75rem" }}>
                  <li>
                    {t(
                      "botChannels.whatsapp.pinModal.step1",
                      "Open WhatsApp Manager and go to Account tools → Phone numbers."
                    )}
                  </li>
                  <li>
                    {t(
                      "botChannels.whatsapp.pinModal.step2",
                      "Click the gear icon next to your phone number and open Two-step verification."
                    )}
                  </li>
                  <li>
                    {t(
                      "botChannels.whatsapp.pinModal.step3",
                      "If you don't remember the PIN, turn off two-step verification from there (you'll get an email), then set a new 6-digit PIN."
                    )}
                  </li>
                  <li>
                    {t(
                      "botChannels.whatsapp.pinModal.step4",
                      "Come back here and enter that exact 6-digit PIN."
                    )}
                  </li>
                </ol>

                <label className="form-field" style={{ marginTop: "1rem" }}>
                  <span>{t("botChannels.whatsapp.numberLabel")}</span>
                  <select
                    value={waSelectedNumberId}
                    onChange={(e) => setWaSelectedNumberId(e.target.value)}
                  >
                    <option value="">
                      {t("botChannels.whatsapp.selectPlaceholder")}
                    </option>
                    {waSession.numbers.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.displayPhoneNumber || n.id}
                        {n.verifiedName ? ` – ${n.verifiedName}` : ""}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="form-field">
                  <span>
                    {t(
                      "botChannels.whatsapp.pinLabel",
                      "WhatsApp two-step verification PIN"
                    )}
                  </span>
                  <input
                    type="text"
                    maxLength={6}
                    value={waRegistrationPin}
                    onChange={(e) =>
                      setWaRegistrationPin(
                        e.target.value.replace(/\D/g, "")
                      )
                    }
                    placeholder={t(
                      "botChannels.whatsapp.pinPlaceholder",
                      "6-digit PIN set in WhatsApp Manager"
                    )}
                  />
                  <p className="muted small" style={{ marginTop: "0.25rem" }}>
                    {t(
                      "botChannels.whatsapp.pinModal.pinHint",
                      "Enter exactly the same 6-digit PIN you see under Two-step verification for this phone number in WhatsApp Manager."
                    )}
                  </p>
                </label>
              </div>

              <div className="meta-page-select-modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setWaSession(null);
                    setWaSelectedNumberId("");
                    setWaRegistrationPin("");
                  }}
                >
                  {t("botChannels.actions.cancel")}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    !waSelectedNumberId ||
                    !isValidWaPin ||
                    waAttachLoading
                  }
                  onClick={handleAttachWhatsappSession}
                >
                  {waAttachLoading
                    ? t("botChannels.actions.connecting")
                    : t("botChannels.actions.connectSelected")}
                </button>
              </div>
            </div>
          </div>
        )}

        <div>
          <h1>{t("botChannels.title")}</h1>
          <p className="muted">{t("botChannels.subtitle")}</p>
        </div>
        <Link to={`/app/bots/${id}`} className="btn-secondary">
          {t("botChannels.backToBot")}
        </Link>
      </div>

      {error && <div className="form-error">{error}</div>}

      {loading && <p>{t("botChannels.loading")}</p>}

      {/* Meta + WhatsApp connect cards */}
      {!loading && (
        <section className="detail-main" style={{ marginBottom: "1.5rem" }}>
          <h2>{t("botChannels.connectSocial.title")}</h2>
          <p className="muted">{t("botChannels.connectSocial.subtitle")}</p>

          <div className="channel-cards">
            {/* Facebook card */}
            <article className="channel-card">
              <div className="channel-card-header">
                <div className="channel-card-main">
                  <div className="channel-card-icon channel-card-icon-facebook">
                    f
                  </div>
                  <div>
                    <h3 className="channel-card-title">
                      {t("botChannels.labels.facebookPage")}
                    </h3>
                    <p className="channel-card-description">
                      {t("botChannels.connectSocial.facebookDesc")}
                    </p>
                  </div>
                </div>
                {renderChannelStatusBadge(facebookChannel)}
              </div>

              {facebookChannel && (
                <p className="channel-card-meta">
                  {t("botChannels.connectSocial.connectedPage")}:{" "}
                  <strong>
                    {(facebookChannel.meta as any)?.pageName ||
                      t("botChannels.meta.connectedViaMeta")}
                  </strong>
                </p>
              )}

              {!fbStatus.isConnected && (
                <button
                  type="button"
                  className="btn-primary channel-card-button"
                  onClick={() => openMetaPermissionsDialog("FACEBOOK")}
                >
                  {facebookChannel
                    ? t("botChannels.actions.reconnectFacebook")
                    : t("botChannels.actions.connectFacebook")}
                </button>
              )}

              {facebookChannel && (
                <button
                  type="button"
                  className="btn-secondary channel-card-button"
                  onClick={() => handleDelete(facebookChannel.id)}
                >
                  {t("botChannels.actions.disconnect")}
                </button>
              )}
            </article>

            {/* Instagram card */}
            <article className="channel-card">
              <div className="channel-card-header">
                <div className="channel-card-main">
                  <div className="channel-card-icon channel-card-icon-instagram">
                    ig
                  </div>
                  <div>
                    <h3 className="channel-card-title">
                      {t("botChannels.labels.instagramBusiness")}
                    </h3>
                    <p className="channel-card-description">
                      {t("botChannels.connectSocial.instagramDesc")}
                    </p>
                  </div>
                </div>
                {renderChannelStatusBadge(instagramChannel)}
              </div>

              {instagramChannel && (
                <p className="channel-card-meta">
                  {t("botChannels.connectSocial.connectedAccount")}:{" "}
                  <strong>
                    {(() => {
                      const meta = (instagramChannel.meta as any) || {};
                      return (
                        meta.igUsername ||
                        meta.igName ||
                        meta.pageName ||
                        t("botChannels.meta.connectedViaMeta")
                      );
                    })()}
                  </strong>
                </p>
              )}

              {!igStatus.isConnected && (
                <button
                  type="button"
                  className="btn-primary channel-card-button"
                  onClick={() => openMetaPermissionsDialog("INSTAGRAM")}
                >
                  {instagramChannel
                    ? t("botChannels.actions.reconnectInstagram")
                    : t("botChannels.actions.connectInstagram")}
                </button>
              )}

              {instagramChannel && (
                <button
                  type="button"
                  className="btn-secondary channel-card-button"
                  onClick={() => handleDelete(instagramChannel.id)}
                >
                  {t("botChannels.actions.disconnect")}
                </button>
              )}
            </article>

            {/* WhatsApp card */}
            <article className="channel-card">
              <div className="channel-card-header">
                <div className="channel-card-main">
                  <div className="channel-card-icon channel-card-icon-whatsapp">
                    wa
                  </div>
                  <div>
                    <h3 className="channel-card-title">
                      {t("botChannels.labels.whatsappBusiness")}
                    </h3>
                    <p className="channel-card-description">
                      {t("botChannels.connectSocial.whatsappDesc")}
                    </p>
                  </div>
                </div>
                {renderChannelStatusBadge(whatsappChannel)}
              </div>

              {whatsappChannel && (
                <p className="channel-card-meta">
                  {t("botChannels.connectSocial.connectedNumber")}:{" "}
                  <strong>
                    {(() => {
                      const meta = (whatsappChannel.meta as any) || {};
                      return (
                        meta.displayPhoneNumber ||
                        meta.display_phone_number ||
                        whatsappChannel.externalId
                      );
                    })()}
                  </strong>
                </p>
              )}

              {!waStatus.isConnected && (
                <button
                  type="button"
                  className="btn-primary channel-card-button"
                  onClick={handleConnectWhatsApp}
                  disabled={waConnecting}
                >
                  {waConnecting
                    ? t("botChannels.actions.connectingWhatsapp")
                    : whatsappChannel
                    ? t("botChannels.actions.reconnectWhatsapp")
                    : t("botChannels.actions.connectWhatsapp")}
                </button>
              )}

              {whatsappChannel && (
                <button
                  type="button"
                  className="btn-secondary channel-card-button"
                  onClick={() => handleDelete(whatsappChannel.id)}
                >
                  {t("botChannels.actions.disconnect")}
                </button>
              )}
            </article>
          </div>
        </section>
      )}

      {/* Connected channels list + manual channels */}
      {!loading && (
        <section className="detail-main">
          <h2>{t("botChannels.connected.title")}</h2>
          <p className="muted">{t("botChannels.connected.subtitle")}</p>

          {channels.length === 0 ? (
            <p className="muted">{t("botChannels.connected.empty")}</p>
          ) : (
            <div className="card" style={{ marginTop: "0.75rem" }}>
              <div className="table-responsive connected-channels-table-wrapper">
                <table className="table connected-channels-table">
                  <thead>
                    <tr>
                      <th>{connectedChannelLabels.type}</th>
                      <th>{connectedChannelLabels.details}</th>
                      <th>{connectedChannelLabels.status}</th>
                      <th className="connected-channels-actions-header">
                        {connectedChannelLabels.actions}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {channels.map((ch) => (
                      <tr key={ch.id}>
                        <td data-label={connectedChannelLabels.type}>
                          {ch.type}
                        </td>
                        <td data-label={connectedChannelLabels.details}>
                          {renderChannelDetails(ch)}
                        </td>
                        <td data-label={connectedChannelLabels.status}>
                          {renderChannelStatusBadge(ch)}
                        </td>
                        <td
                          className="connected-channels-actions-cell"
                          data-label={connectedChannelLabels.actions}
                        >
                          {editingId === ch.id ? (
                            <>
                              <button
                                className="btn-secondary"
                                type="button"
                                onClick={() => setEditingId(null)}
                                style={{ marginRight: "0.5rem" }}
                              >
                                {t("botChannels.actions.cancel")}
                              </button>
                            </>
                          ) : (
                            !isMetaChannelType(ch.type) && (
                              <button
                                className="btn-secondary"
                                type="button"
                                onClick={() => startEdit(ch)}
                                style={{ marginRight: "0.5rem" }}
                              >
                                {t("botChannels.actions.edit")}
                              </button>
                            )
                          )}

                          <button
                            className="btn-danger"
                            type="button"
                            onClick={() => handleDelete(ch.id)}
                          >
                            {t("botChannels.actions.disconnect")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Edit form */}
          {editingId && (
            <div className="card" style={{ marginTop: "1rem" }}>
              <h3 style={{ marginTop: 0 }}>{t("botChannels.edit.title")}</h3>
              <form className="form" onSubmit={handleEditSubmit}>
                <label className="form-field">
                  <span>{t("botChannels.fields.externalId")}</span>
                  <input
                    type="text"
                    value={editForm.externalId}
                    onChange={handleEditChange("externalId")}
                  />
                </label>

                <label className="form-field">
                  <span>{t("botChannels.fields.accessToken")}</span>
                  <input
                    type="text"
                    value={editForm.accessToken}
                    onChange={handleEditChange("accessToken")}
                  />
                </label>

                <label className="form-field">
                  <span>{t("botChannels.fields.metaJson")}</span>
                  <textarea
                    value={editForm.meta}
                    onChange={handleEditChange("meta")}
                    rows={6}
                    placeholder={t("botChannels.placeholders.metaJson")}
                  />
                </label>

                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button className="btn-primary" type="submit">
                    {t("botChannels.actions.save")}
                  </button>
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => setEditingId(null)}
                  >
                    {t("botChannels.actions.cancel")}
                  </button>
                </div>
              </form>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default BotChannelsPage;
